import { arenas, CONTENT_VERSION, fighters, type FighterDef } from './content'
import { heroSkillName } from './heroSkills'
import type { AttackKind } from './simulation'
import { formatHudTimer, hudAriaMax, hudAriaValue, hudMeterPercent, hudStatusFlags } from './hudMath'
import { useFightStore } from './store'

function Meter({
  value,
  max,
  reverse = false,
  kind = 'health',
  testId,
}: {
  value: number
  max: number
  reverse?: boolean
  kind?: 'health' | 'stamina' | 'super' | 'guard'
  testId?: string
}) {
  const pct = hudMeterPercent(value, max)
  return (
    <div
      className={`meter ${kind} ${reverse ? 'reverse' : ''}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={hudAriaMax(max)}
      aria-valuenow={hudAriaValue(value, max)}
      data-testid={testId}
    >
      <div style={{ width: `${pct}%` }} />
    </div>
  )
}

function SetPips({ wins, reverse = false }: { wins: number; reverse?: boolean }) {
  return (
    <div
      aria-label={`${wins} round wins`}
      style={{ display: 'flex', gap: 5, justifyContent: reverse ? 'flex-end' : 'flex-start', marginTop: 7 }}
    >
      {[0, 1].map((i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{
            width: 18,
            height: 3,
            display: 'block',
            background: i < wins ? '#d9fbff' : 'rgba(129,157,181,.25)',
            boxShadow: i < wins ? '0 0 10px rgba(100,231,255,.7)' : 'none',
            transform: 'skewX(-24deg)',
          }}
        />
      ))}
    </div>
  )
}

function StatusFlags({
  health,
  maxHealth,
  superMeter,
  guardMeter,
  stamina,
  reverse = false,
}: {
  health: number
  maxHealth: number
  superMeter: number
  guardMeter: number
  stamina: number
  reverse?: boolean
}) {
  const { critical, superReady, guardDanger, staminaLow } = hudStatusFlags(
    health,
    maxHealth,
    superMeter,
    guardMeter,
    stamina,
  )

  if (!critical && !superReady && !guardDanger && !staminaLow) return null

  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        gap: 8,
        justifyContent: reverse ? 'flex-end' : 'flex-start',
        marginTop: 6,
        font: '700 8px Orbitron,sans-serif',
        letterSpacing: '.18em',
      }}
    >
      {critical && <span style={{ color: '#ff997a', textShadow: '0 0 12px rgba(255,90,55,.7)' }}>CRITICAL</span>}
      {guardDanger && (
        <span style={{ color: '#ffd17a', textShadow: '0 0 12px rgba(255,180,70,.7)' }}>GUARD DANGER</span>
      )}
      {staminaLow && (
        <span style={{ color: '#8fffd1', textShadow: '0 0 12px rgba(90,255,195,.55)' }}>STAMINA LOW</span>
      )}
      {superReady && (
        <span style={{ color: '#d9b2ff', textShadow: '0 0 12px rgba(190,110,255,.7)' }}>SUPER READY</span>
      )}
    </div>
  )
}

function SkillCue({ fighter, attack, reverse = false }: { fighter: FighterDef; attack: AttackKind | null; reverse?: boolean }) {
  if (!attack) return null
  return (
    <div
      data-testid={reverse ? 'enemy-skill' : 'player-skill'}
      style={{
        marginTop: 7,
        textAlign: reverse ? 'right' : 'left',
        font: '800 8px Orbitron,sans-serif',
        letterSpacing: '.12em',
        color: fighter.accent,
        textShadow: `0 0 14px ${fighter.accent}`,
      }}
    >
      <span style={{ opacity: .58, marginRight: 6 }}>{attack.toUpperCase()}</span>
      {heroSkillName(fighter, attack)}
    </div>
  )
}

function MoveLegend({ fighter, keys }: { fighter: FighterDef; keys: { light: string; heavy: string; special: string; throw: string } }) {
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span><kbd>{keys.light}</kbd> {heroSkillName(fighter, 'light')}</span>
      <span><kbd>{keys.heavy}</kbd> {heroSkillName(fighter, 'heavy')}</span>
      <span style={{ color: fighter.accent }}><kbd>{keys.special}</kbd> {heroSkillName(fighter, 'special')}</span>
      <span><kbd>{keys.throw}</kbd> {heroSkillName(fighter, 'throw')}</span>
    </span>
  )
}

export function Hud() {
  const s = useFightStore()
  const p = fighters[s.playerIndex]
  const e = fighters[s.enemyIndex]
  const arena = arenas[s.arenaIndex]
  const roundFinished = s.status === 'round-over' || s.status === 'match-over'
  const matchFinished = s.status === 'match-over'
  const winner = s.winner === 'player' ? p : s.winner === 'enemy' ? e : null
  const local = s.mode === 'local'
  const matchPoint = !matchFinished && (s.playerWins === 1 || s.enemyWins === 1)
  const matchPointLabel =
    s.playerWins === 1 && s.enemyWins === 1
      ? 'DOUBLE MATCH POINT'
      : s.playerWins === 1
        ? 'P1 MATCH POINT'
        : `${local ? 'P2' : 'RIVAL'} MATCH POINT`

  return (
    <div
      className={`hud ${roundFinished ? 'round-finished' : ''} ${matchFinished ? 'match-finished' : ''} ${s.paused ? 'is-paused' : ''}`}
    >
      <header className="topbar">
        <div className="fighter-card left">
          <span className="eyebrow">P1 · {p.title}</span>
          <strong>{p.name}</strong>
          <SetPips wins={s.playerWins} />
          <StatusFlags
            health={s.player.health}
            maxHealth={s.player.maxHealth}
            superMeter={s.player.superMeter}
            guardMeter={s.player.guardMeter}
            stamina={s.player.stamina}
          />
          <Meter value={s.player.health} max={s.player.maxHealth} testId="player-health" />
          <div className="micro">
            <Meter value={s.player.stamina} max={100} kind="stamina" testId="player-stamina" />
            <Meter value={s.player.guardMeter} max={100} kind="guard" testId="player-guard" />
            <Meter value={s.player.superMeter} max={100} kind="super" testId="player-super" />
          </div>
          {s.player.comboCount > 1 && (
            <div className="combo left-combo" data-testid="player-combo">
              {s.player.comboCount} HIT COMBO
            </div>
          )}
        </div>

        <div className="clock">
          <span>{matchPoint ? matchPointLabel : `BEST OF 3 · ROUND ${s.round}`}</span>
          <strong data-testid="round-timer">{formatHudTimer(s.timer)}</strong>
          <small>
            {s.playerWins} — {s.enemyWins}
          </small>
        </div>

        <div className="fighter-card right">
          <span className="eyebrow">
            {local ? 'P2' : 'CPU'} · {e.title}
          </span>
          <strong>{e.name}</strong>
          <SetPips wins={s.enemyWins} reverse />
          <StatusFlags
            health={s.enemy.health}
            maxHealth={s.enemy.maxHealth}
            superMeter={s.enemy.superMeter}
            guardMeter={s.enemy.guardMeter}
            stamina={s.enemy.stamina}
            reverse
          />
          <Meter value={s.enemy.health} max={s.enemy.maxHealth} reverse testId="enemy-health" />
          <div className="micro">
            <Meter value={s.enemy.stamina} max={100} kind="stamina" reverse testId="enemy-stamina" />
            <Meter value={s.enemy.guardMeter} max={100} kind="guard" reverse testId="enemy-guard" />
            <Meter value={s.enemy.superMeter} max={100} kind="super" reverse testId="enemy-super" />
          </div>
          {s.enemy.comboCount > 1 && <div className="combo right-combo">{s.enemy.comboCount} HIT COMBO</div>}
        </div>
      </header>

      {roundFinished && (
        <div className="cinematic-finish" aria-hidden="true">
          <div className="finish-beam" />
          <div className="finish-ring ring-a" />
          <div className="finish-ring ring-b" />
        </div>
      )}

      {s.paused && !roundFinished && (
        <div className="pause-state">
          <span>COMBAT SUSPENDED</span>
          <strong>PAUSED</strong>
        </div>
      )}

      {s.banner && (
        <div className={`banner ${roundFinished ? 'finish-banner' : ''}`} data-testid="fight-banner">
          <span>{roundFinished ? 'DECISIVE IMPACT' : 'COMBAT PROTOCOL'}</span>
          <strong>{s.banner}</strong>
          {roundFinished && (
            <small>{matchFinished ? (winner ? `${winner.name} · VICTORY` : 'DRAW · NO ASCENDANT') : `ROUND ${s.round} COMPLETE`}</small>
          )}
        </div>
      )}

      {matchFinished && winner && (
        <div className="victory-card">
          <span>RIFT ASCENDANT</span>
          <strong>{winner.name}</strong>
          <small>{winner.title}</small>
          <div className="victory-line" />
        </div>
      )}

      <div className="arena-label">
        <span>{arena.region}</span>
        <strong>{arena.name}</strong>
      </div>

      <footer className="controls">
        {local ? (
          <>
            <div>
              P1 · <MoveLegend fighter={p} keys={{ light: 'J', heavy: 'K', special: 'L', throw: 'U' }} />
            </div>
            <div>
              P2 · <MoveLegend fighter={e} keys={{ light: '1', heavy: '2', special: '3', throw: '4' }} />
            </div>
          </>
        ) : (
          <>
            <div>
              P1 · <MoveLegend fighter={p} keys={{ light: 'J', heavy: 'K', special: 'L', throw: 'U' }} />
            </div>
            <div>GAMEPAD · LS/D-PAD mover · RB+LS↑/↓ depth · LT/↓ defesa · FACE ataques · LB throw · B/RB dash</div>
          </>
        )}
      </footer>
      <div className="build-tag">AAA R3F VX · {CONTENT_VERSION}</div>
    </div>
  )
}
